import { Button, Heading } from '@altify/components';
import { useContent } from '@altify/utils';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import RecurringBuyDetailsModal from 'app/_authenticated/recurring-buys/components/RecurringBuyDetailsModal/RecurringBuyDetailsModal';
import { RecurringBuyList } from 'app/_authenticated/recurring-buys/components/RecurringBuyList/RecurringBuyList';
import RecurringBuyResumeModal from 'app/_authenticated/recurring-buys/components/RecurringBuyResumeModal/RecurringBuyResumeModal';
import { sharedContent } from 'app/_authenticated/recurring-buys/recurringBuyShared.content';
import { RecurringBuySchedule } from 'app/_authenticated/trade/recurring-buy/models/RecurringBuySchedule';
import BackButton from 'components/BackButton/BackButton';
import { useFunctionalCurrency, useProducts } from 'hooks/products/useProducts';
import { useState } from 'react';
import {
  useCancelRecurringBuyMutation,
  useGetRecurringBuysQuery,
  usePauseRecurringBuyMutation,
  useResumeRecurringBuyMutation,
} from 'store/slices/recurringBuy-api';
import { lowerCase } from 'utils/casing';
import { formatCurrency } from 'utils/currencies';
import { RecurringBuyListSkeleton } from './components/RecurringBuyListSkeleton';
import { content } from './route.content';

export const Route = createFileRoute('/_authenticated/recurring-buys')({
  component: RecurringBuyOverviewScreen,
});

function RecurringBuyOverviewScreen() {
  const navigate = useNavigate();
  const { functionalCurrency } = useFunctionalCurrency();
  const localizedContent = useContent(content, sharedContent);

  const [recurringBuyId, setRecurringBuyId] = useState('');
  const [recurringBuyAmount, setRecurringBuyAmount] = useState(0);
  const [recurringBuyProduct, setRecurringBuyProduct] = useState('');
  const [recurringBuySchedule, setRecurringBuySchedule] = useState('');
  const [recurringBuyNextExecution, setRecurringBuyNextExecution] =
    useState('');

  const [isResumeModalOpen, setIsResumeModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  const { products } = useProducts();
  const [resumeRecurringBuy, resumeRecurringBuyResult] =
    useResumeRecurringBuyMutation();

  const recurringBuyQuery = useGetRecurringBuysQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const [pauseRecurringBuy, pauseRecurringBuyResult] =
    usePauseRecurringBuyMutation();

  const [cancelRecurringBuy, cancelRecurringBuyResult] =
    useCancelRecurringBuyMutation();

  const recurringBuys =
    recurringBuyQuery.data?.map(rb => ({
      id: rb.recurringBuyId,
      productCode: rb.productCode,
      productName: products?.find(p => p.code === rb.productCode)?.name ?? '',
      amount: formatCurrency(rb.amount, functionalCurrency?.code!),
      paused: rb.paused,
      interval: localizedContent.intervalReadable({
        interval: lowerCase(rb.interval),
        dayOfWeek: rb.dayOfWeek,
        dayOfMonth: rb.dayOfMonth,
        buyNow: false,
      } as RecurringBuySchedule),
    })) ?? [];
  const pausedRecurringBuys = recurringBuys?.filter(b => b.paused);
  const activeRecurringBuys = recurringBuys?.filter(b => !b.paused);

  async function handleRecurringBuyResume(
    recurringBuyId: string,
    recurringBuyAmount: number,
    recurringBuyProduct: string,
    recurringBuySchedule: string,
    recurringBuyNextExecution: string,
  ) {
    setRecurringBuyAmount(recurringBuyAmount);
    setRecurringBuyProduct(recurringBuyProduct);
    setRecurringBuyNextExecution(recurringBuyNextExecution);
    setRecurringBuySchedule(recurringBuySchedule);
    setIsDetailsModalOpen(false);
    setIsResumeModalOpen(true);
    await resumeRecurringBuy(recurringBuyId);
  }

  function handleRecurringBuyDetails(recurringBuyId: string) {
    setRecurringBuyId(recurringBuyId);
    setIsDetailsModalOpen(true);
  }

  async function handleCancelRecurringBuy() {
    await cancelRecurringBuy(recurringBuyId);
    setIsDetailsModalOpen(false);
  }

  async function handlePauseRecurringBuy() {
    await pauseRecurringBuy(recurringBuyId);
    setIsDetailsModalOpen(false);
  }

  return (
    <div className="h-full flex flex-col px-8 w-full">
      <BackButton
        className={
          'w-full text-left mt-1 ml-0 pl-6 lg:mt-2 pt-2 lg:ml-0 lg:pl-0 lg:pt-0'
        }
        onClick={() => navigate({ to: '/settings' })}
      />
      <Heading level={2} className="md:mt-6 lg:mt-10 text-center">
        {localizedContent.recurringBuys}
      </Heading>
      {recurringBuyQuery?.isLoading ? (
        <>
          <div className="hidden lg:flex flex-row justify-evenly gap-8">
            <RecurringBuyListSkeleton />
            <RecurringBuyListSkeleton />
          </div>
          <div className="flex lg:hidden flex-row justify-evenly gap-8">
            <RecurringBuyListSkeleton />
          </div>
        </>
      ) : (
        <>
          <div className="mt-8 w-full lg:grid md:grid-cols-1 lg:gap-x-8 lg:gap-y-16 lg:grid-cols-2 overflow-y-auto lg:overflow-hidden h-dvh">
            <RecurringBuyList
              isActiveList={true}
              list={activeRecurringBuys}
              onShowRecurringBuyDetails={(recurringBuyId: string) =>
                handleRecurringBuyDetails(recurringBuyId)
              }
            />
            <RecurringBuyList
              isActiveList={false}
              list={pausedRecurringBuys}
              onShowRecurringBuyDetails={(recurringBuyId: string) =>
                handleRecurringBuyDetails(recurringBuyId)
              }
            />
          </div>
          <div className="mt-4 mb-12 flex lg:hidden">
            <Button
              color="primary"
              text={localizedContent.createRecurringBuy}
              className="w-full"
              onPress={() => {
                navigate({ to: '/trade/recurring-buy' });
              }}
            />
          </div>
          <div className="flex flex-col items-center max-w-[500px] w-full">
            {isDetailsModalOpen && (
              <RecurringBuyDetailsModal
                isOpen={isDetailsModalOpen}
                recurringBuyId={recurringBuyId}
                onClose={() => setIsDetailsModalOpen(false)}
                onCancelRecurringBuy={handleCancelRecurringBuy}
                onPauseRecurringBuy={handlePauseRecurringBuy}
                onResumeRecurringBuy={async (
                  amount: number,
                  productCode: string,
                  schedule: string,
                  nextExecution: string,
                ) =>
                  handleRecurringBuyResume(
                    recurringBuyId,
                    amount,
                    productCode,
                    schedule,
                    nextExecution,
                  )
                }
                cancelIsLoading={cancelRecurringBuyResult?.isLoading}
                manageIsLoading={
                  pauseRecurringBuyResult?.isLoading ||
                  resumeRecurringBuyResult.isLoading
                }
              />
            )}
            {isResumeModalOpen && (
              <RecurringBuyResumeModal
                schedule={recurringBuySchedule}
                onClose={() => {
                  setIsResumeModalOpen(false);
                }}
                resumeComplete={resumeRecurringBuyResult.isSuccess}
                resumeError={!!resumeRecurringBuyResult.error}
                fromAmount={recurringBuyAmount}
                toProductCode={recurringBuyProduct}
                fromProductCode={functionalCurrency?.code!}
                nextExecution={recurringBuyNextExecution}
                onCancelResume={() => {
                  setIsResumeModalOpen(false);
                }}
                onRetryResume={() => {
                  resumeRecurringBuy(recurringBuyId);
                }}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
export default RecurringBuyOverviewScreen;
